import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import { isApiSuccess } from '@/api/shared.types.ts';
import {
  getVoucherDetailsById,
  getVoucherBatchesSummary,
} from '@/api/voucher/voucher.actions.ts';
import { VoucherDetails, VoucherBatch } from '@/api/voucher/voucher.types.ts';
import { useAsync } from '@/hooks/global/useAsync';

const DEFAULT_PAGE_SIZE = 10;
const DEBOUNCE_DELAY = 500;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useVouchers = (pageSize = DEFAULT_PAGE_SIZE) => {
  const [voucherBatchesCount, setVoucherBatchesCount] = useState(0);
  const [voucherBatches, setVoucherBatches] = useState<VoucherBatch[]>([]);
  const [voucherDetails, setVoucherDetails] = useState<VoucherDetails>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
  const { run, isLoading: isFetchingAll } = useAsync();

  const fetchVoucherBatchesSummary = async (page = 1): Promise<void> => {
    const response = await run(() =>
      getVoucherBatchesSummary(page, pageSize, debouncedSearchText)
    );

    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      return;
    }

    setVoucherBatches(response.content.data);
    setVoucherBatchesCount(response.content.pagination.total);
  };

  const fetchVoucherDetails = async (id: string): Promise<VoucherDetails> => {
    const response = await getVoucherDetailsById(id);

    if (!isApiSuccess(response)) {
      toast.error(response.error.message);
      setVoucherDetails(null);
      return;
    }

    setVoucherDetails(response.content.voucherDetails);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, DEBOUNCE_DELAY);

    return (): void => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    fetchVoucherBatchesSummary(currentPage);
  }, [currentPage, debouncedSearchText]);

  return {
    voucherBatchesCount,
    voucherBatches,
    voucherDetails,
    setVoucherDetails,
    refetch: fetchVoucherBatchesSummary,
    fetchVoucherDetails,
    isFetchingAll,
    pageSize,
    currentPage,
    setCurrentPage,
    searchText,
    setSearchText,
  };
};
